import React, { useState, useEffect, useCallback } from 'react';
import { usersApi, rolesApi } from '../api';
import { useAuth } from '../AuthContext';

interface User {
  id: number;
  username: string;
  email: string;
  nickname: string;
  phone: string;
  is_active: boolean;
  is_superuser: boolean;
  roles: { id: number; code: string; name: string }[];
  created_at: string;
  last_login: string | null;
}

interface Role {
  id: number;
  code: string;
  name: string;
  description: string;
}

interface Props {
  onClose: () => void;
}

const UserManagement: React.FC<Props> = ({ onClose }) => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');

  // 弹窗状态
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showResetPwdModal, setShowResetPwdModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // 创建用户表单
  const [newUser, setNewUser] = useState({ username: '', password: '', nickname: '', email: '', phone: '' });
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([]);
  const [newPassword, setNewPassword] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (search) params.search = search;
      const res = await usersApi.getAll(params);
      setUsers(res.data.results || res.data || []);
    } catch (err: any) {
      setError(err.response?.data?.error || '加载用户列表失败');
    } finally {
      setLoading(false);
    }
  }, [search]);

  const loadRoles = useCallback(async () => {
    try {
      const res = await rolesApi.getAll();
      setRoles(res.data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadUsers(); loadRoles(); }, [loadUsers, loadRoles]);

  const handleCreate = async () => {
    if (!newUser.username || !newUser.password) { setError('用户名和密码必填'); return; }
    setActionLoading(true);
    try {
      await usersApi.create(newUser);
      setShowCreateModal(false);
      setNewUser({ username: '', password: '', nickname: '', email: '', phone: '' });
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || '创建失败');
    } finally { setActionLoading(false); }
  };

  const handleToggleActive = async (u: User) => {
    try {
      await usersApi.toggleActive(u.id);
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || '操作失败');
    }
  };

  const handleDelete = async (u: User) => {
    if (!window.confirm(`确定删除用户 "${u.username}" 吗？此操作不可恢复。`)) return;
    try {
      await usersApi.delete(u.id);
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || '删除失败');
    }
  };

  const openRoleModal = (u: User) => {
    setSelectedUser(u);
    setSelectedRoleIds(u.roles.map(r => r.id));
    setShowRoleModal(true);
  };

  const handleSetRoles = async () => {
    if (!selectedUser) return;
    setActionLoading(true);
    try {
      await usersApi.setRoles(selectedUser.id, selectedRoleIds);
      setShowRoleModal(false);
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || '设置角色失败');
    } finally { setActionLoading(false); }
  };

  const openResetPwdModal = (u: User) => {
    setSelectedUser(u);
    setNewPassword('');
    setShowResetPwdModal(true);
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) { setError('请输入新密码'); return; }
    setActionLoading(true);
    try {
      await usersApi.resetPassword(selectedUser.id, newPassword);
      setShowResetPwdModal(false);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.error || '重置密码失败');
    } finally { setActionLoading(false); }
  };

  const toggleRoleId = (id: number) => {
    setSelectedRoleIds(prev => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);
  };

  const formatDate = (d: string | null) => {
    if (!d) return '从未';
    return new Date(d).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="um-page">
      {/* Header */}
      <div className="um-header">
        <div className="um-header-bg" />
        <div className="um-header-inner">
          <button className="um-back" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <h1>用户管理</h1>
          <button className="um-add-btn" onClick={() => setShowCreateModal(true)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="um-body">
        {/* Search */}
        <div className="um-search-wrap">
          <span className="um-search-icon">🔍</span>
          <input
            className="um-search"
            placeholder="搜索用户名、昵称..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {error && <div className="um-error">{error} <button onClick={() => setError('')}>✕</button></div>}

        {loading ? (
          <div className="um-loading"><div className="um-spinner" /><p>加载中...</p></div>
        ) : users.length === 0 ? (
          <div className="um-empty">暂无用户</div>
        ) : (
          <div className="um-list">
            {users.map(u => (
              <div key={u.id} className={`um-card ${!u.is_active ? 'disabled' : ''}`}>
                <div className="um-card-top">
                  <div className="um-avatar">{(u.nickname || u.username).charAt(0).toUpperCase()}</div>
                  <div className="um-info">
                    <div className="um-name">
                      {u.nickname || u.username}
                      {u.is_superuser && <span className="um-badge super">超管</span>}
                      {!u.is_active && <span className="um-badge inactive">已禁用</span>}
                    </div>
                    <div className="um-meta">@{u.username} · 最后登录: {formatDate(u.last_login)}</div>
                  </div>
                </div>
                <div className="um-roles">
                  {u.roles.length > 0 ? u.roles.map(r => (
                    <span key={r.id} className="um-role-tag">{r.name}</span>
                  )) : <span className="um-no-role">无角色</span>}
                </div>
                {u.id !== currentUser?.id && (
                  <div className="um-actions">
                    <button className="um-act-btn" onClick={() => openRoleModal(u)}>🏷️ 角色</button>
                    <button className="um-act-btn" onClick={() => openResetPwdModal(u)}>🔑 密码</button>
                    <button className="um-act-btn" onClick={() => handleToggleActive(u)}>
                      {u.is_active ? '🚫 禁用' : '✅ 启用'}
                    </button>
                    {!u.is_superuser && (
                      <button className="um-act-btn danger" onClick={() => handleDelete(u)}>🗑️ 删除</button>
                    )}
                  </div>
                )}
                {u.id === currentUser?.id && (
                  <div className="um-actions"><span className="um-self-tag">当前用户</span></div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="um-modal-overlay" onClick={e => e.target === e.currentTarget && setShowCreateModal(false)}>
          <div className="um-modal">
            <div className="um-modal-header">
              <h3>创建用户</h3>
              <button className="um-modal-close" onClick={() => setShowCreateModal(false)}>✕</button>
            </div>
            <div className="um-modal-body">
              <div className="um-field"><label>用户名 *</label><input value={newUser.username} onChange={e => setNewUser(prev => ({...prev, username: e.target.value}))} placeholder="请输入用户名" /></div>
              <div className="um-field"><label>密码 *</label><input type="password" value={newUser.password} onChange={e => setNewUser(prev => ({...prev, password: e.target.value}))} placeholder="请输入密码" /></div>
              <div className="um-field"><label>昵称</label><input value={newUser.nickname} onChange={e => setNewUser(prev => ({...prev, nickname: e.target.value}))} placeholder="可选" /></div>
              <div className="um-field"><label>邮箱</label><input value={newUser.email} onChange={e => setNewUser(prev => ({...prev, email: e.target.value}))} placeholder="可选" /></div>
              <div className="um-field"><label>手机</label><input value={newUser.phone} onChange={e => setNewUser(prev => ({...prev, phone: e.target.value}))} placeholder="可选" /></div>
            </div>
            <div className="um-modal-footer">
              <button className="um-btn secondary" onClick={() => setShowCreateModal(false)}>取消</button>
              <button className="um-btn primary" onClick={handleCreate} disabled={actionLoading}>{actionLoading ? '创建中...' : '创建'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Role Modal */}
      {showRoleModal && selectedUser && (
        <div className="um-modal-overlay" onClick={e => e.target === e.currentTarget && setShowRoleModal(false)}>
          <div className="um-modal">
            <div className="um-modal-header">
              <h3>设置角色 - {selectedUser.nickname || selectedUser.username}</h3>
              <button className="um-modal-close" onClick={() => setShowRoleModal(false)}>✕</button>
            </div>
            <div className="um-modal-body">
              {roles.map(r => (
                <label key={r.id} className="um-checkbox-row">
                  <input type="checkbox" checked={selectedRoleIds.includes(r.id)} onChange={() => toggleRoleId(r.id)} />
                  <div>
                    <div className="um-checkbox-label">{r.name} <span className="um-checkbox-code">({r.code})</span></div>
                    <div className="um-checkbox-desc">{r.description}</div>
                  </div>
                </label>
              ))}
            </div>
            <div className="um-modal-footer">
              <button className="um-btn secondary" onClick={() => setShowRoleModal(false)}>取消</button>
              <button className="um-btn primary" onClick={handleSetRoles} disabled={actionLoading}>{actionLoading ? '保存中...' : '保存'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPwdModal && selectedUser && (
        <div className="um-modal-overlay" onClick={e => e.target === e.currentTarget && setShowResetPwdModal(false)}>
          <div className="um-modal">
            <div className="um-modal-header">
              <h3>重置密码 - {selectedUser.nickname || selectedUser.username}</h3>
              <button className="um-modal-close" onClick={() => setShowResetPwdModal(false)}>✕</button>
            </div>
            <div className="um-modal-body">
              <div className="um-field"><label>新密码</label><input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="请输入新密码（至少6位）" /></div>
            </div>
            <div className="um-modal-footer">
              <button className="um-btn secondary" onClick={() => setShowResetPwdModal(false)}>取消</button>
              <button className="um-btn primary" onClick={handleResetPassword} disabled={actionLoading}>{actionLoading ? '重置中...' : '重置密码'}</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .um-page { min-height: 100vh; background: var(--bg); display: flex; flex-direction: column; }
        .um-header { position: relative; overflow: hidden; }
        .um-header-bg { position: absolute; inset: 0; background: linear-gradient(135deg, #7C3AED 0%, #8B5CF6 50%, #6366F1 100%); }
        .um-header-bg::after { content: ''; position: absolute; bottom: -20px; left: -10%; right: -10%; height: 50px; background: var(--bg); border-radius: 50% 50% 0 0; }
        .um-header-inner { position: relative; z-index: 1; display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; padding-top: calc(16px + var(--status-bar-height, 28px)); padding-bottom: 28px; }
        .um-header-inner h1 { margin: 0; font-size: 20px; font-weight: 700; color: white; }
        .um-back, .um-add-btn { width: 36px; height: 36px; border-radius: 50%; background: rgba(255,255,255,0.2); border: none; color: white; display: flex; align-items: center; justify-content: center; cursor: pointer; backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px); transition: all 200ms; }
        .um-back:active, .um-add-btn:active { transform: scale(0.9); background: rgba(255,255,255,0.3); }

        .um-body { flex: 1; padding: 0 20px 20px; margin-top: -8px; }
        .um-search-wrap { position: relative; margin-bottom: 16px; }
        .um-search-icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); font-size: 14px; }
        .um-search { width: 100%; padding: 12px 16px 12px 40px; border: 1.5px solid var(--border); border-radius: var(--radius-md); font-size: 14px; font-family: inherit; background: var(--surface); color: var(--text-primary); box-sizing: border-box; }
        .um-search:focus { outline: none; border-color: var(--primary); }

        .um-error { background: var(--danger-bg); color: var(--danger); padding: 12px 16px; border-radius: var(--radius-md); font-size: 14px; margin-bottom: 16px; display: flex; justify-content: space-between; align-items: center; }
        .um-error button { background: none; border: none; color: var(--danger); cursor: pointer; font-size: 16px; }

        .um-loading { display: flex; flex-direction: column; align-items: center; padding: 60px 20px; gap: 12px; }
        .um-spinner { width: 32px; height: 32px; border: 3px solid var(--border); border-top-color: var(--primary); border-radius: 50%; animation: spin 0.8s linear infinite; }
        .um-loading p { color: var(--text-tertiary); font-size: 14px; }
        .um-empty { text-align: center; padding: 60px 20px; color: var(--text-tertiary); font-size: 14px; }

        .um-list { display: flex; flex-direction: column; gap: 12px; }
        .um-card { background: var(--surface); border-radius: var(--radius-lg); padding: 16px; box-shadow: var(--shadow-sm); border: 1px solid var(--border-light); }
        .um-card.disabled { opacity: 0.6; }
        .um-card-top { display: flex; align-items: center; gap: 12px; margin-bottom: 10px; }
        .um-avatar { width: 42px; height: 42px; border-radius: 50%; background: linear-gradient(135deg, #8B5CF6, #6366F1); color: white; display: flex; align-items: center; justify-content: center; font-size: 18px; font-weight: 600; flex-shrink: 0; }
        .um-info { flex: 1; min-width: 0; }
        .um-name { font-size: 15px; font-weight: 600; color: var(--text-primary); display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
        .um-badge { font-size: 11px; padding: 2px 8px; border-radius: 20px; font-weight: 500; }
        .um-badge.super { background: var(--amber-bg); color: var(--amber); }
        .um-badge.inactive { background: var(--danger-bg); color: var(--danger); }
        .um-meta { font-size: 12px; color: var(--text-tertiary); margin-top: 2px; }
        .um-roles { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 10px; }
        .um-role-tag { font-size: 12px; padding: 3px 10px; border-radius: 20px; background: var(--primary-bg); color: var(--primary); font-weight: 500; }
        .um-no-role { font-size: 12px; color: var(--text-tertiary); }
        .um-actions { display: flex; flex-wrap: wrap; gap: 8px; }
        .um-act-btn { padding: 6px 12px; border: 1px solid var(--border); border-radius: var(--radius-sm); background: var(--surface); font-size: 12px; font-family: inherit; color: var(--text-secondary); cursor: pointer; transition: all 150ms; }
        .um-act-btn:active { transform: scale(0.95); background: var(--surface-hover); }
        .um-act-btn.danger { color: var(--danger); border-color: var(--danger-bg); }
        .um-act-btn.danger:active { background: var(--danger-bg); }
        .um-self-tag { font-size: 12px; color: var(--text-tertiary); font-style: italic; }

        /* Modals */
        .um-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.4); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px; animation: fadeIn 200ms; }
        .um-modal { background: var(--surface); border-radius: var(--radius-xl); width: 100%; max-width: 420px; max-height: 85vh; overflow: hidden; display: flex; flex-direction: column; animation: scaleIn 300ms cubic-bezier(0.34, 1.56, 0.64, 1); box-shadow: 0 20px 60px rgba(0,0,0,0.15); }
        .um-modal-header { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid var(--border-light); }
        .um-modal-header h3 { margin: 0; font-size: 17px; font-weight: 600; }
        .um-modal-close { width: 32px; height: 32px; border: none; background: var(--surface-hover); border-radius: 50%; font-size: 16px; color: var(--text-secondary); cursor: pointer; display: flex; align-items: center; justify-content: center; }
        .um-modal-close:active { transform: scale(0.9); }
        .um-modal-body { padding: 20px; overflow-y: auto; flex: 1; }
        .um-modal-footer { padding: 16px 20px; border-top: 1px solid var(--border-light); display: flex; gap: 10px; justify-content: flex-end; }

        .um-field { margin-bottom: 16px; }
        .um-field label { display: block; font-size: 12px; font-weight: 600; color: var(--text-tertiary); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 6px; }
        .um-field input { width: 100%; padding: 11px 14px; border: 1.5px solid var(--border); border-radius: var(--radius-md); font-size: 14px; font-family: inherit; color: var(--text-primary); background: var(--bg); box-sizing: border-box; }
        .um-field input:focus { outline: none; border-color: var(--primary); background: var(--surface); }

        .um-checkbox-row { display: flex; align-items: flex-start; gap: 12px; padding: 12px 0; border-bottom: 1px solid var(--border-light); cursor: pointer; }
        .um-checkbox-row:last-child { border-bottom: none; }
        .um-checkbox-row input[type="checkbox"] { margin-top: 3px; width: 18px; height: 18px; accent-color: var(--primary); }
        .um-checkbox-label { font-size: 14px; font-weight: 500; color: var(--text-primary); }
        .um-checkbox-code { font-size: 12px; color: var(--text-tertiary); }
        .um-checkbox-desc { font-size: 12px; color: var(--text-secondary); margin-top: 2px; }

        .um-btn { padding: 10px 20px; border: none; border-radius: var(--radius-md); font-size: 14px; font-weight: 500; font-family: inherit; cursor: pointer; transition: all 200ms; }
        .um-btn.primary { background: linear-gradient(135deg, var(--primary), var(--fuchsia)); color: white; box-shadow: var(--shadow-primary); }
        .um-btn.primary:active { transform: scale(0.97); }
        .um-btn.primary:disabled { opacity: 0.6; cursor: not-allowed; }
        .um-btn.secondary { background: var(--surface-hover); color: var(--text-secondary); }
        .um-btn.secondary:active { background: var(--border); }

        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        @keyframes spin { to { transform: rotate(360deg); } }

        @media (min-width: 768px) { .um-body { max-width: 600px; margin-left: auto; margin-right: auto; } }
      `}</style>
    </div>
  );
};

export default UserManagement;
